import { useFloatingTree, useListItem } from "@floating-ui/react";
import {
  ButtonHTMLAttributes,
  MouseEvent,
  forwardRef,
  useContext,
  FocusEvent,
  ComponentPropsWithoutRef,
  ReactNode,
} from "react";
import cx from "classnames";
import { MenuLegacyContext } from "./MenuLegacy";
import styles from "./MenuLegacy.module.scss";
import { Icon, IconProps } from "../Icon";
import { childrenToString } from "../../functions";
import { useMergeRefs } from "../../hooks";

export type MenuLegacyItemProps = {
  icon?: IconProps["svg"];
  label: ReactNode;
  disabled?: boolean;
} & ComponentPropsWithoutRef<"button">;

export const MenuLegacyItem = forwardRef<
  HTMLButtonElement,
  MenuLegacyItemProps & ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, disabled, icon, className, ...props }, forwardedRef) => {
  const MenuLegacy = useContext(MenuLegacyContext);
  const item = useListItem({
    label: disabled ? null : childrenToString(label),
  });
  const tree = useFloatingTree();
  const isActive = item.index === MenuLegacy.activeIndex;
  const itemCx = cx([styles["item"]], className);
  return (
    <button
      {...props}
      ref={useMergeRefs([item.ref, forwardedRef])}
      type="button"
      role="menuitem"
      data-anv="menu-item"
      className={itemCx}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      {...MenuLegacy.getItemProps({
        onClick(event: MouseEvent<HTMLButtonElement>) {
          props.onClick?.(event);
          tree?.events.emit("click");
        },
        onFocus(event: FocusEvent<HTMLButtonElement>) {
          props.onFocus?.(event);
          MenuLegacy.setHasFocusInside?.(true);
        },
      })}
    >
      {icon ? <Icon inherit aria-hidden svg={icon} /> : null}
      {label}
    </button>
  );
});

MenuLegacyItem.displayName = "MenuLegacyItem";
